import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRightIcon, 
  ArrowDownIcon, 
  ArrowUpIcon, 
  CurrencyDollarIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon 
} from '@heroicons/react/outline';
import useAuthStore from '../../store/authStore';

// Dummy data for demonstration
const dummyWallet = {
  balance: 12500,
  pending: 3500,
  available: 9000,
  minWithdrawal: 10000,
};

const dummyTransactions = [
  { id: 1, contract: 'عقد #1234', client: 'أحمد محمد', amount: 3500, date: '2025-06-20', status: 'تم التحصيل', type: 'ايداع' },
  { id: 2, contract: 'عقد #1233', client: 'سارة خالد', amount: 2500, date: '2025-06-18', status: 'قيد الانتظار', type: 'ايداع' },
  { id: 3, contract: 'عقد #1232', client: 'محمد علي', amount: 3000, date: '2025-06-15', status: 'تم التحصيل', type: 'ايداع' },
  { id: 4, contract: 'عقد #1231', client: 'نورة سعد', amount: 5000, date: '2025-06-10', status: 'تم التحصيل', type: 'ايداع' },
];

const dummyWithdrawals = [
  { id: 1, amount: 10000, date: '2025-05-28', status: 'مكتمل', reference: 'REF123456' },
  { id: 2, amount: 18500, date: '2025-04-15', status: 'مكتمل', reference: 'REF789012' },
];

const FinanceManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const handleWithdraw = () => {
    if (!withdrawAmount) return;
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 10000) {
      alert('الحد الأدنى للسحب 10,000 ريال');
      return;
    }
    if (amount > dummyWallet.available) {
      alert('المبلغ المتاح للسحب غير كافي');
      return;
    }
    
    setIsWithdrawing(true);
    // Simulate API call
    setTimeout(() => {
      alert(`تم طلب سحب مبلغ ${amount.toLocaleString()} ريال بنجاح`);
      setIsWithdrawing(false);
      setWithdrawAmount('');
    }, 1500);
  };

  if (userData?.role !== 'lawyer') {
    return (
      <div className="p-5 text-center">
        <p>غير مصرح بالوصول لهذه الصفحة</p>
      </div>
    );
  }

  return (
    <div className="p-5 bg-gray-50 min-h-screen text-right dir-rtl font-tajawal mb-24">
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-200"
        >
          <ArrowRightIcon className="h-5 w-5 text-gray-700" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mr-4">الإدارة المالية</h2>
      </div>

      {/* Wallet Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-2xl shadow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-500">الرصيد الكلي</h3>
            <CurrencyDollarIcon className="h-6 w-6 text-blue-500" />
          </div>
          <p className="text-2xl font-bold">{dummyWallet.balance.toLocaleString()} ريال</p>
        </div>
        
        <div className="bg-white p-5 rounded-2xl shadow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-500">المتاح للسحب</h3>
            <ArrowUpIcon className="h-6 w-6 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">{dummyWallet.available.toLocaleString()} ريال</p>
        </div>
        
        <div className="bg-white p-5 rounded-2xl shadow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-500">قيد الانتظار</h3>
            <ClockIcon className="h-6 w-6 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-600">{dummyWallet.pending.toLocaleString()} ريال</p>
        </div>
      </div>

      {/* Withdrawal Section */}
      <div className="bg-white p-5 rounded-2xl shadow mb-6">
        <h3 className="text-lg font-bold mb-4">طلب سحب الأموال</h3>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">المبلغ المطلوب سحبه</label>
            <div className="relative">
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
                placeholder="أدخل المبلغ"
                dir="rtl"
              />
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">ر.س</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">الحد الأدنى للسحب 10,000 ريال</p>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleWithdraw}
              disabled={isWithdrawing || dummyWallet.available < dummyWallet.minWithdrawal}
              style={{
                backgroundColor: dummyWallet.available >= dummyWallet.minWithdrawal ? 'oklch(0.55 0.24 262.91)' : '#9ca3af',
                color: '#ffffff',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                fontWeight: 500,
                border: 'none',
                cursor: dummyWallet.available >= dummyWallet.minWithdrawal && !isWithdrawing ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s',
                fontSize: '1rem',
                fontFamily: 'inherit',
                outline: 'none',
                opacity: isWithdrawing ? 0.8 : 1,
                width: '100%',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={e => {
                if (dummyWallet.available >= dummyWallet.minWithdrawal && !isWithdrawing) {
                  e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)';
                }
              }}
              onMouseOut={e => {
                if (dummyWallet.available >= dummyWallet.minWithdrawal) {
                  e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)';
                } else {
                  e.currentTarget.style.backgroundColor = '#9ca3af';
                }
              }}
            >
              {isWithdrawing ? 'جاري المعالجة...' : 'طلب سحب الأموال'}
            </button>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3 px-5">آخر العمليات المالية</h3>
        <div className="space-y-2 px-3">
          {dummyTransactions.map((tx) => (
            <div key={tx.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-gray-900">{tx.contract}</p>
                    <p className="text-sm text-gray-500">{tx.client}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{tx.amount.toLocaleString()} ريال</p>
                    <p className="text-xs text-gray-400">{tx.date}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-50">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    tx.status === 'تم التحصيل' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                  }`}>
                    {tx.status === 'تم التحصيل' ? (
                      <CheckCircleIcon className="ml-1 h-3.5 w-3.5" />
                    ) : (
                      <ClockIcon className="ml-1 h-3.5 w-3.5" />
                    )}
                    {tx.status}
                  </span>
                  <button className="text-blue-500 text-sm font-medium">
                    التفاصيل
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Withdrawal History */}
      <div>
        <h3 className="text-lg font-bold mb-3 px-5">سجل عمليات السحب</h3>
        <div className="space-y-2 px-3">
          {dummyWithdrawals.map((withdrawal) => (
            <div key={withdrawal.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-gray-900">طلب سحب #{withdrawal.id}</p>
                    <p className="text-sm text-gray-500">المرجع: {withdrawal.reference}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-red-600">-{withdrawal.amount.toLocaleString()} ريال</p>
                    <p className="text-xs text-gray-400">{withdrawal.date}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-50">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                    <CheckCircleIcon className="ml-1 h-3.5 w-3.5" />
                    {withdrawal.status}
                  </span>
                  <button className="text-blue-500 text-sm font-medium">
                    تفاصيل العملية
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FinanceManagementPage;
